import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Login from "../pages/Login";
import AccessDenied from "../pages/AccessDenied";
import APITest from "../pages/APITest";
import TriloChat from "../pages/TriloChat";
import CSRSummary from "../pages/csr/Summary";
import LaySummary from "../pages/csr/LaySummary";
import CSRFileUpload from "../pages/csr/FileUpload";
import SCSSummary from "../pages/scs/Summary";
import SCSFileUpload from "../pages/scs/FileUpload";
import SCSDescriptionOfStudies from "../pages/scs/DescriptionOfStudies";
import SCSSubjectDisposition from "../pages/scs/SubjectDisposition";
import SCSOverallExtentOfExposure from "../pages/scs/OverallExtentOfExposure";
import SCSAnalysisOfAdverseEvents from "../pages/scs/AnalysisOfAdverseEvents";
import SCSClinicalLaboratoryEvaluations from "../pages/scs/ClinicalLaboratoryEvaluations";
import SCSSafetyInSpecialGroups from "../pages/scs/SafetyInSpecialGroups";
import SCSPostMarketingData from "../pages/scs/PostMarketingData";
import SCSDataMapping from "../pages/scs/DataMapping";
import SCSFile from "../pages/scs/ScsFile";
import LSQuestions from "../pages/csr/LSQuestions";
import SelectStudy from "../pages/csr/SelectStudy";
import DataMapping from "../pages/csr/DataMapping";
import CSRFile from "../pages/csr/File";
import PNSummary from "../pages/pn/Summary";
import PNFileUpload from "../pages/pn/FileUpload";
import PNProjectConventions from "../pages/pn/ProjectConventions";
import PNPatientScope from "../pages/pn/PatientScope";
import PNSources from "../pages/pn/Sources";
import PNDataMapping from "../pages/pn/DataMapping";
import PNNarrativeFormat from "../pages/pn/NarrativeFormat";
import LaySummaryFile from "../pages/csr/LaySummaryFile";
import StandardPharmacokinetics from "../pages/csr/StandardPharmacokinetics";
import ComparativePharmacokinetics from "../pages/csr/ComparativePharmacokinetics";
import Pharmacodynamics from "../pages/csr/Pharmacodynamics";
import StandardPharmacodynamics from "../pages/csr/StandardPharmacodynamics";
import ComparativePharmacodynamics from "../pages/csr/ComparativePharmacodynamics";
import Bioavailability from "../pages/csr/Bioavailability";
import StudyDesignDemographics from "../pages/csr/StudyDesignDemographics";
import EfficacyAnalysis from "../pages/csr/EfficacyAnalysis";
import SafetyAnalysis from "../pages/csr/SafetyAnalysis";
import TableAnalysisQuestions from "../pages/csr/TableAnalysisQuestions";
import TableAnalysisAIResponse from "../pages/csr/TableAnalysisAIResponse";
import CsrProvider from "../contexts/CsrProvider";
import QuestionsProvider from "../contexts/QuestionsProvider";
import ProtectedComponent from "../utils/ProtectedComponent";
import LoginForbiddenGate from "../components/LoginForbiddenGate";
import { PermissionsProvider } from "../contexts/PermissionsContext";
import Loader from "../utils/Loader";

// The pages that render an AG Grid are split into their own chunks so the
// grid library (~2MB) is only downloaded when one of them is visited, not on
// every page load.
const Dashboard = lazy(() => import("../pages/Dashboard"));
const PNFile = lazy(() => import("../pages/pn/File"));
const AdvancedSearch = lazy(() => import("../pages/csr/AdvancedSearch"));

const isProduction = window.location.hostname === "app.trilodocs.com";

function AppRoutes() {
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Outside the gate: the page a refused login is sent to must stay
          reachable once loginForbidden is set. */}
        <Route path="access-denied" element={<AccessDenied />} />
        <Route element={<LoginForbiddenGate />}>
          <Route path="/" element={<Login />} />
          {/* Outside PermissionsProvider: a 404 from GET /me/permissions sends
            the user here, and rendering this page inside the provider would
            only fire the same failing request again. */}
          <Route path="not-found" element={<NotFound />} />
          <Route element={<PermissionsProvider />}>
            <Route element={<CsrProvider />}>
              <Route
                path="dashboard"
                element={<ProtectedComponent component={Dashboard} />}
              />
              {!isProduction && (
                <Route
                  path="apitest"
                  element={<ProtectedComponent component={APITest} />}
                />
              )}
              <Route
                path="trilochat"
                element={<ProtectedComponent component={TriloChat} />}
              />
              <Route path="csr">
                <Route index element={<NotFound />} />
                <Route path=":studyId">
                  <Route index element={<NotFound />} />
                  <Route
                    path="summary"
                    element={<ProtectedComponent component={CSRSummary} />}
                  />
                  <Route
                    path="select-study"
                    element={<ProtectedComponent component={SelectStudy} />}
                  />
                  <Route
                    path="lay-summary"
                    element={<ProtectedComponent component={LaySummary} />}
                  />
                  <Route
                    path="file-upload"
                    element={<ProtectedComponent component={CSRFileUpload} />}
                  />
                  <Route element={<QuestionsProvider />}>
                    <Route
                      path="study-design-demographics"
                      element={
                        <ProtectedComponent
                          component={StudyDesignDemographics}
                        />
                      }
                    />
                    <Route
                      path="efficacy-analysis"
                      element={
                        <ProtectedComponent component={EfficacyAnalysis} />
                      }
                    />
                    <Route
                      path="safety-analysis"
                      element={
                        <ProtectedComponent component={SafetyAnalysis} />
                      }
                    />
                    <Route
                      path="stdpharmacokinetics"
                      element={
                        <ProtectedComponent
                          component={StandardPharmacokinetics}
                        />
                      }
                    />
                    <Route
                      path="comppharmacokinetics"
                      element={
                        <ProtectedComponent
                          component={ComparativePharmacokinetics}
                        />
                      }
                    />
                    <Route
                      path="pharmacodynamics"
                      element={
                        <ProtectedComponent component={Pharmacodynamics} />
                      }
                    />
                    <Route
                      path="stdpharmacodynamics"
                      element={
                        <ProtectedComponent
                          component={StandardPharmacodynamics}
                        />
                      }
                    />
                    <Route
                      path="comppharmacodynamics"
                      element={
                        <ProtectedComponent
                          component={ComparativePharmacodynamics}
                        />
                      }
                    />
                    <Route
                      path="bioavailability"
                      element={
                        <ProtectedComponent component={Bioavailability} />
                      }
                    />
                  </Route>
                  <Route element={<QuestionsProvider />}>
                    <Route
                      path="lay-summary-questions"
                      element={<ProtectedComponent component={LSQuestions} />}
                    />
                  </Route>
                  <Route element={<QuestionsProvider />}>
                    <Route
                      path="data-mapping"
                      element={<ProtectedComponent component={DataMapping} />}
                    />
                  </Route>
                  <Route
                    path="file"
                    element={<ProtectedComponent component={CSRFile} />}
                  />
                  <Route
                    path="lay-summary-file"
                    element={<ProtectedComponent component={LaySummaryFile} />}
                  />
                  <Route
                    path="advancedsearch"
                    element={<ProtectedComponent component={AdvancedSearch} />}
                  />
                  <Route element={<QuestionsProvider />}>
                    <Route
                      path="table-analysis-questions/:tableId"
                      element={
                        <ProtectedComponent
                          component={TableAnalysisQuestions}
                        />
                      }
                    />
                  </Route>
                  <Route
                    path="table-analysis-ai-response/:tableId"
                    element={
                      <ProtectedComponent component={TableAnalysisAIResponse} />
                    }
                  />
                </Route>
                <Route
                  path="new/summary"
                  element={<ProtectedComponent component={CSRSummary} />}
                />
                <Route
                  path="new/select-study"
                  element={<ProtectedComponent component={SelectStudy} />}
                />
              </Route>
              <Route path="scs">
                <Route index element={<NotFound />} />
                <Route path=":studyId">
                  <Route index element={<NotFound />} />
                  <Route
                    path="summary"
                    element={<ProtectedComponent component={SCSSummary} />}
                  />
                  <Route
                    path="file-upload"
                    element={<ProtectedComponent component={SCSFileUpload} />}
                  />
                  <Route
                    path="description-of-studies"
                    element={
                      <ProtectedComponent component={SCSDescriptionOfStudies} />
                    }
                  />
                  <Route
                    path="subject-disposition"
                    element={
                      <ProtectedComponent component={SCSSubjectDisposition} />
                    }
                  />
                  <Route
                    path="overall-extent-of-exposure"
                    element={
                      <ProtectedComponent
                        component={SCSOverallExtentOfExposure}
                      />
                    }
                  />
                  <Route
                    path="analysis-of-adverse-events"
                    element={
                      <ProtectedComponent
                        component={SCSAnalysisOfAdverseEvents}
                      />
                    }
                  />
                  <Route
                    path="clinical-laboratory-evaluations"
                    element={
                      <ProtectedComponent
                        component={SCSClinicalLaboratoryEvaluations}
                      />
                    }
                  />
                  <Route
                    path="safety-in-special-groups"
                    element={
                      <ProtectedComponent
                        component={SCSSafetyInSpecialGroups}
                      />
                    }
                  />
                  <Route
                    path="post-marketing-data"
                    element={
                      <ProtectedComponent component={SCSPostMarketingData} />
                    }
                  />
                  <Route
                    path="data-mapping"
                    element={<ProtectedComponent component={SCSDataMapping} />}
                  />
                  <Route
                    path="scs-file"
                    element={<ProtectedComponent component={SCSFile} />}
                  />
                </Route>
                <Route
                  path="new/summary"
                  element={<ProtectedComponent component={SCSSummary} />}
                />
              </Route>
              <Route path="pn">
                <Route index element={<NotFound />} />
                <Route path=":studyId">
                  <Route index element={<NotFound />} />
                  <Route
                    path="summary"
                    element={<ProtectedComponent component={PNSummary} />}
                  />
                  <Route
                    path="file-upload"
                    element={<ProtectedComponent component={PNFileUpload} />}
                  />
                  <Route
                    path="project-conventions"
                    element={
                      <ProtectedComponent component={PNProjectConventions} />
                    }
                  />
                  <Route
                    path="patient-scope"
                    element={<ProtectedComponent component={PNPatientScope} />}
                  />
                  <Route
                    path="sources"
                    element={<ProtectedComponent component={PNSources} />}
                  />
                  <Route
                    path="data-mapping"
                    element={<ProtectedComponent component={PNDataMapping} />}
                  />
                  <Route
                    path="narrative-format"
                    element={
                      <ProtectedComponent component={PNNarrativeFormat} />
                    }
                  />
                  <Route
                    path="file"
                    element={<ProtectedComponent component={PNFile} />}
                  />
                </Route>
                <Route
                  path="new/summary"
                  element={<ProtectedComponent component={PNSummary} />}
                />
              </Route>
              <Route path="ls">
                <Route index element={<NotFound />} />
                <Route path=":studyId">
                  <Route index element={<NotFound />} />
                  <Route
                    path="select-study"
                    element={<ProtectedComponent component={SelectStudy} />}
                  />
                  <Route
                    path="lay-summary"
                    element={<ProtectedComponent component={LaySummary} />}
                  />
                  <Route element={<QuestionsProvider />}>
                    <Route
                      path="lay-summary-questions"
                      element={<ProtectedComponent component={LSQuestions} />}
                    />
                  </Route>
                  <Route
                    path="lay-summary-file"
                    element={<ProtectedComponent component={LaySummaryFile} />}
                  />
                </Route>
                <Route
                  path="new/select-study"
                  element={<ProtectedComponent component={SelectStudy} />}
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
